import type { SessionState, ComputedMetrics } from '@/state/types.ts';

// Calculate all metrics for a completed session
export function calculateMetrics(state: SessionState): ComputedMetrics {
  const B = state.baselineMood;
  const C = state.clipCheckins.map((c) => c.mood);
  const S = state.silenceCheckins.map((s) => s.mood);

  // Calculate preClip moods
  const preClip: number[] = [
    0, // index 0 unused
    B, // preClip[1] = baseline
    C[0] || B, // preClip[2] = C[1]
    S[0] || B, // preClip[3] = S[1] (after silence 1)
    C[2] || B, // preClip[4] = C[3] (after shift/stay choice)
    S[1] || B, // preClip[5] = S[2] (after silence 2)
    C[4] || B, // preClip[6] = C[5]
  ];

  const reactionStrength = calculateReactionStrength(C, preClip);
  const residualEffect = calculateResidualEffect(S, B);
  const shiftEffect = calculateShiftEffect(S, B);

  return {
    reactionStrength,
    residualEffect,
    shiftEffect,
  };
}

// Reaction Strength = average absolute change after each clip
export function calculateReactionStrength(C: number[], preClip: number[]): number {
  if (!C.length) return 0;
  let sum = 0;
  for (let i = 0; i < C.length; i++) {
    const clipMood = C[i];
    const baseline = preClip[i + 1] ?? preClip[0] ?? 5;
    sum += Math.abs(clipMood - baseline);
  }
  return sum / C.length;
}

// Residual Effect = average difference from baseline after silences
export function calculateResidualEffect(S: number[], B: number): number {
  if (S.length < 3) return 0;

  let sum = 0;
  for (let j = 0; j < 3; j++) {
    const silenceMood = S[j] || B;
    sum += silenceMood - B;
  }

  return sum / 3;
}

// Shift Effect = remaining effect after choice vs. other silences
export function calculateShiftEffect(S: number[], B: number): number {
  if (S.length < 3) return 0;

  // Remaining effect after silence 2 (which follows the shift/stay clip)
  const remainingAfterChoice = Math.abs((S[1] || B) - B);

  // Remaining effect average of silences 1 and 3 (non-choice silences)
  const remainingOther = (Math.abs((S[0] || B) - B) + Math.abs((S[2] || B) - B)) / 2;

  return remainingAfterChoice - remainingOther;
}

// Check if mood recovered faster after the shift/stay choice
export function checkRecoveredFaster(
  state: SessionState,
  shiftChoice: 'shift' | 'stay'
): boolean {
  if (shiftChoice !== 'shift') return false;

  const B = state.baselineMood;
  const C = state.clipCheckins.map((c) => c.mood);
  const S = state.silenceCheckins.map((s) => s.mood);

  if (C.length < 5 || S.length < 2) return false;

  // Recovery after choice = how much the gap closed
  const recoveryAfterChoice = Math.abs((C[3] || B) - B) - Math.abs((S[1] || B) - B);

  // Recovery for silence 1
  const recoveryOther = Math.abs((C[1] || B) - B) - Math.abs((S[0] || B) - B);

  return recoveryAfterChoice > recoveryOther + 0.5;
}

// Format metric for display
export function formatMetric(value: number, decimals = 1): string {
  return value.toFixed(decimals);
}

// Get reaction strength description
export function getReactionStrengthDescription(value: number): string {
  return `Your mood usually changes by about ${formatMetric(value)} points after listening.`;
}

// Get residual effect description
export function getResidualEffectDescription(value: number): string {
  const absValue = Math.abs(value);
  
  if (absValue < 0.5) {
    return 'After silence, your mood returns close to where you started.';
  }
  
  const direction = value > 0 ? 'higher' : 'lower';
  return `After silence, your mood is still about ${formatMetric(absValue)} points ${direction}.`;
}

// Get shift effect description
export function getShiftEffectDescription(
  value: number,
  shiftChoice: 'shift' | 'stay' | null
): string {
  if (shiftChoice === 'shift' && value < -0.5) {
    return 'When you tried to shift, the remaining effect was smaller.';
  }
  
  if (shiftChoice === 'shift' && value >= -0.5) {
    return 'Even when you tried to shift, the emotional effect persisted.';
  }
  
  if (shiftChoice === 'stay') {
    return 'By staying with the feeling, the effect lingered at a similar level.';
  }
  
  return 'The emotional effect varied across different moments.';
}
